import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { io } from "socket.io-client";
import ConfigDB from "../../config";
import {
  listUserNotifications,
  markNotificationRead,
  deleteNotification,
} from "../../services/notificationsServices";

// Bell global de notificações no Header.
//
// 1. Faz fetch inicial a `/user/notifications/recent?unread_only=1` para
//    contar não-lidas (sobrevive a reload do browser).
// 2. Liga Socket.IO ao API host e emite `register_user(userId)` no
//    handshake. Backend (`sendCompanyAlert`) emite `notification:new`
//    para o user em todas as sessões activas.
// 3. Quando recebe novo evento: toast informativo + incrementa badge.
const POLL_FALLBACK_MS = 5 * 60 * 1000; // safety net se socket cair

const NotificationsBell = () => {
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [recent, setRecent] = useState([]);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const socketRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const r = await listUserNotifications({ limit: 10 });
      const data = r?.data?.data || {};
      setRecent(data.rows || []);
      setUnread(Number(data.counts?.total || 0));
    } catch (_) {
      // silencioso (sessão expirada etc.)
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, POLL_FALLBACK_MS);
    return () => clearInterval(id);
  }, [refresh]);

  // Socket.IO live updates
  useEffect(() => {
    let userId = null;
    try {
      const raw = localStorage.getItem(ConfigDB.data.login_data);
      if (raw) userId = JSON.parse(raw)?.id;
    } catch (_) {}
    if (!userId) return;

    const apiUrl = ConfigDB.data.api_url || "";
    const socketHost = apiUrl.replace(/\/api\/v\d+\/?$/, "");
    if (!socketHost) return;

    const socket = io(socketHost, {
      transports: ["websocket", "polling"],
      reconnection: true,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      socket.emit("register_user", userId);
    });

    socket.on("notification:new", (payload) => {
      const title = payload?.title || "Notificação";
      const message = payload?.message || "";
      toast.info(`${title}\n${message}`, {
        position: "top-right",
        autoClose: 5000,
      });
      // Optimistic — bump unread imediato + refresh para garantir sync.
      setUnread((c) => c + 1);
      refresh();
    });

    // Outro dispositivo do mesmo user apagou — limpa local sem refresh.
    socket.on("notification:deleted", (payload) => {
      const ids = (payload && payload.ids) || [];
      if (!ids.length) return;
      setRecent((arr) => arr.filter((n) => !ids.includes(n.id)));
      // O contador unread vem do servidor; refresh garante consistência.
      refresh();
    });

    return () => {
      try {
        socket.disconnect();
      } catch (_) {}
    };
  }, [refresh]);

  // Close on outside click / Esc
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const onClickItem = async (item) => {
    setOpen(false);
    try {
      if (!item.read_at) await markNotificationRead(item.id);
    } catch (_) {}
    const data = item?.data || {};
    const type = data.type || item.alert_type;
    const saleId = data.id;
    const taskId = data.task_id;
    const reservationId = data.reservation_id;
    const vehicleId = data.vehicle_id;
    const documentId = data.document_id;
    switch (type) {
      case "sales":
      case "refund":
        navigate(saleId ? `/sales-details/${saleId}` : "/sales");
        break;
      case "moloni_documents":
      case "moloni_documents_info":
      case "invoice_canceled":
      case "invoice_deleted":
      case "credit_note_emitted":
      case "credit_note_partial":
      case "invoice_external_baixa":
      case "invoice_finalized_externally":
        if (documentId) navigate(`/faturacao?search=${encodeURIComponent(documentId)}`);
        else if (type === "moloni_documents_info") navigate("/faturacao");
        else navigate("/stock-alerts");
        break;
      case "reservation_expiring":
        navigate(reservationId ? `/show-reserves-list?id=${reservationId}` : "/reserves");
        break;
      case "task_assigned":
        navigate(taskId ? `/tasks?id=${taskId}` : "/tasks");
        break;
      case "dismantle_completed":
        navigate(vehicleId ? `/vehicle/details/${vehicleId}` : "/vehicle");
        break;
      default:
        navigate("/profile/notifications");
    }
    refresh();
  };

  if (unread === 0 && recent.length === 0) return null;

  return (
    <li
      className="nav-item"
      style={{ position: "relative", marginRight: "8px", display: "flex", alignItems: "center" }}
      ref={dropdownRef}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={`${unread} notificação${unread === 1 ? "" : "ões"} não lida${unread === 1 ? "" : "s"}`}
        style={{
          background: unread > 0 ? "#eef0fe" : "transparent",
          border: unread > 0 ? "1px solid #c8cdfb" : "1px solid #d8dbe7",
          color: "#3f51b5",
          borderRadius: 6,
          padding: "4px 10px",
          position: "relative",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: 13,
        }}
      >
        <i className="bi bi-bell-fill" style={{ color: "#3f51b5" }} />
        {unread > 0 && (
          <span
            style={{
              backgroundColor: "#3f51b5",
              color: "#fff",
              borderRadius: 10,
              padding: "0 6px",
              fontSize: 11,
              minWidth: 18,
              textAlign: "center",
            }}
          >
            {unread}
          </span>
        )}
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            width: 360,
            maxHeight: 480,
            overflowY: "auto",
            background: "#fff",
            border: "1px solid #e0e3eb",
            borderRadius: 10,
            boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
            zIndex: 1050,
          }}
        >
          <div
            style={{
              padding: "12px 14px",
              borderBottom: "1px solid #eef",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <strong style={{ fontSize: 14 }}>Notificações</strong>
            <button
              type="button"
              className="btn btn-sm btn-link p-0"
              style={{ fontSize: 12 }}
              onClick={() => {
                setOpen(false);
                navigate("/profile/notifications");
              }}
            >
              Ver todas →
            </button>
          </div>
          {recent.length === 0 ? (
            <div style={{ padding: 24, textAlign: "center", color: "#888" }}>
              Sem notificações.
            </div>
          ) : (
            recent.map((n) => (
              <div
                key={n.id}
                onClick={() => onClickItem(n)}
                style={{
                  padding: "10px 14px",
                  borderBottom: "1px solid #f4f5fa",
                  background: !n.read_at ? "#f5f6ff" : "#fff",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 8,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: 13,
                      fontWeight: !n.read_at ? 600 : 400,
                    }}
                  >
                    <span>{n.title}</span>
                    <span style={{ fontSize: 10, color: "#888" }}>
                      {timeAgo(n.sent_at)}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: "#555", marginTop: 3 }}>
                    {n.message}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-link text-muted p-0"
                  title="Apagar"
                  style={{ fontSize: 13, lineHeight: 1 }}
                  onClick={async (e) => {
                    e.stopPropagation();
                    setRecent((arr) => arr.filter((x) => x.id !== n.id));
                    try {
                      await deleteNotification(n.id);
                    } catch (_) {}
                  }}
                >
                  <i className="bi bi-trash" />
                </button>
              </div>
            ))
          )}
        </div>
      )}
    </li>
  );
};

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return "agora";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return d.toLocaleDateString("pt-PT");
}

export default NotificationsBell;
